import Calendar from "../../entities/calendar";

/**
 * Calendar actions
 *
 * Call order:
 *
 * CALENDAR_SAVE_QUEUE:	adds the item to the save queue
 * --> CALENDAR_SAVE:	modifies the state
 * --> CALENDAR_SAVE_SUCCESS: if all went well
 * --> CALENDAR_SAVE_FAILURE:	if either the queue or local save failed
 *
 * Note that the only way to fail CALENDAR_SAVE_QUEUE is during the call to CALENDAR_SAVE,
 * as adding an item to the queue should never fail.
 *
 * OWA-1018: the reducer shows a queued save at CALENDAR_SAVE_QUEUE, marked pending, and only that
 * save's own outcome settles the mark: CALENDAR_SAVE_SUCCESS, CALENDAR_SAVE_REVERT (refused, or
 * never queued), a refusal the queue never retries, or CALENDAR_PENDING_CHECK (it left the queue).
 */

export const CALENDAR_FETCH_ONE_QUEUE: any = 'CALENDAR_FETCH_ONE_QUEUE';
export const CALENDAR_FETCH_SUCCESS: any = 'CALENDAR_FETCH_SUCCESS';
export const CALENDAR_FETCH_FAILURE: any = 'CALENDAR_FETCH_FAILURE';
export const CALENDAR_FETCH_ONE_SUCCESS: any = 'CALENDAR_FETCH_ONE_SUCCESS';
export const CALENDAR_FETCH_ONE_FAILURE: any = 'CALENDAR_FETCH_ONE_FAILURE';

export const CALENDAR_SAVE: any = 'CALENDAR_SAVE';
export const CALENDAR_SAVE_QUEUE: any = 'CALENDAR_SAVE_QUEUE';
export const CALENDAR_SAVE_SUCCESS: any = 'CALENDAR_SAVE_SUCCESS';
export const CALENDAR_SAVE_FAILURE: any = 'CALENDAR_SAVE_FAILURE';

export const CALENDAR_SAVE_MANY: any = 'CALENDAR_SAVE_MANY';
export const CALENDAR_SAVE_MANY_QUEUE: any = 'CALENDAR_SAVE_MANY_QUEUE';
export const CALENDAR_SAVE_MANY_SUCCESS: any = 'CALENDAR_SAVE_MANY_SUCCESS';
export const CALENDAR_SAVE_MANY_FAILURE: any = 'CALENDAR_SAVE_MANY_FAILURE';

export const CALENDAR_REMOVE: any = 'CALENDAR_REMOVE';
export const CALENDAR_REMOVE_QUEUE: any = 'CALENDAR_REMOVE_QUEUE';
export const CALENDAR_REMOVE_SUCCESS: any = 'CALENDAR_REMOVE_SUCCESS';
export const CALENDAR_REMOVE_FAILURE: any = 'CALENDAR_REMOVE_FAILURE';

export const CALENDAR_RESTORE: any = 'CALENDAR_RESTORE';
export const CALENDAR_RESTORE_QUEUE: any = 'CALENDAR_RESTORE_QUEUE';
export const CALENDAR_RESTORE_SUCCESS: any = 'CALENDAR_RESTORE_SUCCESS';
export const CALENDAR_RESTORE_FAILURE: any = 'CALENDAR_RESTORE_FAILURE';

export const CALENDAR_RESTORE_MANY: any = 'CALENDAR_RESTORE_MANY';
export const CALENDAR_REMOVE_MANY: any = 'CALENDAR_REMOVE_MANY';

// OWA-1018: settle this tab's pending save of a record
export const CALENDAR_SAVE_REVERT: any = 'CALENDAR_SAVE_REVERT';
export const CALENDAR_PENDING_CHECK: any = 'CALENDAR_PENDING_CHECK';

/* Fetch actions */
export const fetchCalendarSuccess = (calendars: Calendar[], partial: boolean, lastFetch: number) => ({type: CALENDAR_FETCH_SUCCESS, calendars, partial, lastFetch});
export const fetchCalendarFailure = (err: any) => {
		console.error('CalendarAction: fetchCalendarFailure', err);
		return {type: CALENDAR_FETCH_FAILURE, err};
};
export const fetchOneCalendarQueue = (calendarId: string, sentAddedOn?: number) => ({type: CALENDAR_FETCH_ONE_QUEUE, calendarId, sentAddedOn});
export const fetchOneCalendarSuccess = (calendar: Calendar | null, sentAddedOn?: number, calendarId: string = calendar?.id) => ({type: CALENDAR_FETCH_ONE_SUCCESS, calendarId, calendar, sentAddedOn});
export const fetchOneCalendarFailure = (id: string, err: any) => {
		console.error('CalendarAction: fetchOneCalendarFailure', err);
		return {type: CALENDAR_FETCH_ONE_FAILURE, id, err};
};

/* Save actions */
export const saveCalendar = (calendar: Calendar) => {
    // console.debug('⮂ CALENDAR_SAVE action', calendar?.id);
    return {type: CALENDAR_SAVE, calendar};
}
export const saveCalendarQueue = (calendar: Calendar, onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ CALENDAR_SAVE_QUEUE action', calendar?.id);
    return {type: CALENDAR_SAVE_QUEUE, calendar, onQueued};
}
export const saveCalendarSuccess = (calendar: Calendar, sentAddedOn?: number) => {
    // console.debug('⮂ CALENDAR_SAVE_SUCCESS action', calendar?.id);
    return {type: CALENDAR_SAVE_SUCCESS, calendar, sentAddedOn};
}
export const saveCalendarFailure = (calendar: Calendar, status: number, message: string) => {
    console.warn(`⮂ CALENDAR_SAVE_FAILURE error saving calendar: HTTP ${status}: ${message}`, calendar?.id);
    return {type: CALENDAR_SAVE_FAILURE, calendar, status, message};
};

/* SaveMany actions */
export const saveManyCalendars = (calendars: Calendar[]) => {
    // console.debug('⮂ CALENDAR_SAVE_MANY action', calendars?.map(calendar => calendar?.id).join(','));
    return {type: CALENDAR_SAVE_MANY, calendars};
}
export const saveManyCalendarsQueue = (calendars: Calendar[], onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ CALENDAR_SAVE_MANY_QUEUE action', calendars?.map(calendar => calendar.id).join(','));
    return {type: CALENDAR_SAVE_MANY_QUEUE, calendars, onQueued};
}

/** `sentAddedOns`: the queue key each answer acknowledges, aligned with `calendars` (OWA-1018) */
export const saveManyCalendarsSuccess = (calendars: Calendar[], sentAddedOns?: (number | undefined)[]) => {
    // console.debug('⮂ CALENDAR_SAVE_MANY_SUCCESS action', calendars?.map(calendar => calendar.id).join(','));
    return {type: CALENDAR_SAVE_MANY_SUCCESS, calendars, sentAddedOns};
}
export const saveManyCalendarsFailure = (calendars: Calendar[], status: number, message: string) => {
		console.warn(`⮂ SAVE_MANY_CALENDARS_FAILURE Error saving calendars: HTTP ${status}: ${message}`, calendars?.map(calendar => calendar.id).join(','));
		return {type: CALENDAR_SAVE_MANY_FAILURE, calendars, status, message};
};

/* Remove actions */
export const removeCalendar = (calendar: Calendar) => ({ type: CALENDAR_REMOVE, calendar });
export const removeCalendarQueue = (calendar: Calendar) => ({ type: CALENDAR_REMOVE_QUEUE, calendar });
export const removeCalendarSuccess = (calendar: Calendar) => ({ type: CALENDAR_REMOVE_SUCCESS, calendar });
export const removeCalendarFailure = (calendar: Calendar, status: number, message: string) => {
	console.error(`Redux: removeCalendarFailure Error removing calendar: HTTP ${status}: ${message}`);
	return ({ type: CALENDAR_REMOVE_FAILURE, calendar, status, message });
};

/* Restore actions */
export const restoreCalendar = (calendar: Calendar) => ({ type: CALENDAR_RESTORE, calendar });
export const restoreCalendarQueue = (calendar: Calendar) => ({ type: CALENDAR_RESTORE_QUEUE, calendar });
export const restoreCalendarSuccess = (calendar: Calendar, sentAddedOn?: number) => ({ type: CALENDAR_RESTORE_SUCCESS, calendar, sentAddedOn });
export const restoreCalendarFailure = (calendar: Calendar, status: number, message: string) => {
	console.error(`Redux: restoreCalendarFailure Error restoring calendar: HTTP ${status}: ${message}`);
	return ({ type: CALENDAR_RESTORE_FAILURE, calendar, status, message });
};

/* RestoreMany actions */
export const restoreManyCalendars = (calendars: Calendar[]) => ({ type: CALENDAR_RESTORE_MANY, calendars });

/* RemoveMany actions */
export const removeManyCalendars = (calendars: Calendar[]) => ({ type: CALENDAR_REMOVE_MANY, calendars });

/* Pending save actions (OWA-1018) */
/**
 * The save `calendar` (queue key `sentAddedOn`, default its addedOn) will not be accepted: it was
 * refused, or never reached the queue. Its row returns to the latest accepted version, or to
 * `current` (the record as the server holds it) when that is newer; `keep` leaves the row shown.
 */
export const revertCalendarSave = (calendar: Calendar, options: {sentAddedOn?: number, current?: Calendar | null, keep?: boolean} = {}) =>
    ({type: CALENDAR_SAVE_REVERT, calendar, ...options});
/** processQueue read the shared queue at `readAt` (performance.now()): the live and the permanently refused queue keys */
export const checkCalendarPendingSaves = (live: number[], refused: number[], readAt: number) =>
    ({type: CALENDAR_PENDING_CHECK, live, refused, readAt});